export type MachineStatusDto = 'ACTIVE' | 'ARCHIVED';
export type MachineStatus = 'active' | 'archived';

export interface MachineSummaryDto {
  machine_id: string;
  machine_name: string;
  facility_id: string | null;
  asset_id: string | null;
  machine_type: string | null;
  manufacturer: string | null;
  model: string | null;
  location: string | null;
  production_area: string | null;
  production_line: string | null;
  status: MachineStatusDto;
  version?: number;
  updated_at: string | null;
  status_changed_at?: string | null;
  status_changed_by?: string | null;
  archived_at?: string | null;
  archived_by?: string | null;
  restored_at?: string | null;
  restored_by?: string | null;
}

export interface MachineListResponseDto {
  machines: MachineSummaryDto[];
  next_cursor: string | null;
  request_id?: string;
}

export interface MachineLifecycleResponseDto {
  machine: MachineSummaryDto;
  lifecycle: {
    previous_status: MachineStatusDto;
    status: MachineStatusDto;
    changed_at: string;
    changed_by: string;
  };
  request_id?: string;
}

export interface MachineSummary {
  id: string;
  name: string;
  facilityId: string | null;
  assetId: string | null;
  machineType: string | null;
  manufacturer: string | null;
  model: string | null;
  location: string | null;
  productionArea?: string | null;
  productionLine?: string | null;
  status: MachineStatus;
  version: number;
  updatedAt: string | null;
  statusChangedAt: string | null;
  archivedAt: string | null;
  restoredAt: string | null;
}

export interface CreateMachineInput {
  facilityId: string | null;
  machineName: string;
  machineType: string | null;
  assetId: string | null;
  manufacturer: string | null;
  model: string | null;
  location: string | null;
  productionArea: string | null;
  productionLine: string | null;
  aliases: string[];
  duplicateDecision?: 'CREATE_ANYWAY';
}

export interface MachineCreateResponseDto {
  machine: {
    machine_id: string;
    customer_id: string;
    facility_id: string | null;
    machine_name: string;
    machine_type?: string | null;
    asset_id: string | null;
    manufacturer: string | null;
    model: string | null;
    location: string | null;
    production_area: string | null;
    production_line: string | null;
    aliases: string[];
    status: 'ACTIVE';
  };
  workflow: { workflow_id: string };
  next_step: 'UPLOAD_DOCUMENTS';
}

export interface PossibleDuplicateDto {
  status: 'POSSIBLE_DUPLICATE';
  possible_matches: Array<{
    machine_id: string;
    machine_name: string;
    asset_id: string | null;
    facility_id: string | null;
  }>;
  allowed_actions: Array<'SELECT_EXISTING' | 'CREATE_ANYWAY'>;
  request_id?: string;
}

export type CreateMachineResultDto = MachineCreateResponseDto | PossibleDuplicateDto;

export function isPossibleDuplicate(result: CreateMachineResultDto): result is PossibleDuplicateDto {
  return 'status' in result && result.status === 'POSSIBLE_DUPLICATE';
}

export function mapMachineSummary(dto: MachineSummaryDto): MachineSummary {
  return {
    id: dto.machine_id,
    name: dto.machine_name,
    facilityId: dto.facility_id,
    assetId: dto.asset_id,
    machineType: dto.machine_type,
    manufacturer: dto.manufacturer,
    model: dto.model,
    location: dto.location,
    productionArea: dto.production_area,
    productionLine: dto.production_line,
    status: dto.status === 'ARCHIVED' ? 'archived' : 'active',
    version: dto.version ?? 1,
    updatedAt: dto.updated_at,
    statusChangedAt: dto.status_changed_at ?? null,
    archivedAt: dto.archived_at ?? null,
    restoredAt: dto.restored_at ?? null,
  };
}
